import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { createGoodsReceipt, listGoodsReceipts, listPurchaseOrderLines, listPurchaseOrders, postGoodsReceipt } from '../../api/purchasing';
import { listWarehouses } from '../../api/orgData';
import { usePermissions } from '../../context/PermissionContext';
import { useBranch } from '../../context/BranchContext';
import { GrDetailDrawer } from '../../components/GrDetailDrawer';
import {
  Button,
  ClickableRow,
  EmptyState,
  ErrorNote,
  FieldLabel,
  HintNote,
  ListToolbar,
  LoadingNote,
  Pagination,
  PageHeader,
  Select,
  StatusBadge,
  SummaryStrip,
  TextInput,
  formatDateTime,
  tableStyle,
  tdStyle,
  thStyle,
  usePagination,
} from '../../components/common';
import type { GoodsReceipt, PurchaseOrder, PurchaseOrderLine, Warehouse } from '../../types/domain';

/**
 * Goods Receipts — create (fn_create_goods_receipt, DRAFT) + post
 * (fn_post_goods_receipt_checked -> the unmodified fn_post_goods_receipt,
 * migration 0008). Posting is the one action in the whole vertical slice
 * that actually writes to stock_ledger, which is why it is a separate,
 * explicit step from creation (mirrors goods_receipts.status DRAFT ->
 * POSTED in the schema itself). Every GR number is clickable — it opens a
 * real read-only detail drawer (PO reference, warehouse, status, posted
 * timestamp, lines with qty/UOM) backed by listGoodsReceiptLines.
 *
 * STATUS: IMPLEMENTED_UNVERIFIED — see docs/data-dictionary.md §8k.
 */
export default function GoodsReceiptsPage() {
  const { hasPermission } = usePermissions();
  const { activeBranchId } = useBranch();
  const [grs, setGrs] = useState<GoodsReceipt[]>([]);
  const [allPos, setAllPos] = useState<PurchaseOrder[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [postingId, setPostingId] = useState<string | null>(null);
  const [selectedGr, setSelectedGr] = useState<GoodsReceipt | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');

  const canCreate = hasPermission('goods_receipt', 'create');
  const canPost = hasPermission('goods_receipt', 'post');
  const receivablePos = allPos.filter((po) => po.status === 'ISSUED' || po.status === 'PARTIALLY_RECEIVED');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [grRows, poRows, whRows] = await Promise.all([
        listGoodsReceipts(activeBranchId ?? undefined),
        listPurchaseOrders(activeBranchId ?? undefined),
        listWarehouses(activeBranchId ?? undefined),
      ]);
      setGrs(grRows);
      setAllPos(poRows);
      setWarehouses(whRows);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBranchId]);

  const handlePost = async (grId: string) => {
    setActionError(null);
    setPostingId(grId);
    try {
      await postGoodsReceipt(grId);
      await load();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    } finally {
      setPostingId(null);
    }
  };

  const poNumber = (id: string) => allPos.find((po) => po.po_id === id)?.po_number ?? id;
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return grs.filter((gr) => {
      if (q && !gr.gr_number.toLowerCase().includes(q)) return false;
      if (statusFilter !== 'ALL' && gr.status !== statusFilter) return false;
      return true;
    });
  }, [grs, search, statusFilter]);
  const { pageRows, page, pageCount, setPage, start, pageSize, total } = usePagination(filtered, 15);

  return (
    <div>
      <PageHeader title="Goods Receipts" subtitle="GR: create (DRAFT) lalu post ke Stock Ledger — migration 0007/0008/0032." />
      {canCreate && (
        <Button
          variant="primary"
          onClick={() => setShowForm((v) => !v)}
          style={{ marginBottom: 16 }}
          disabled={receivablePos.length === 0}
          title={receivablePos.length === 0 ? 'Tidak ada PO ISSUED/PARTIALLY_RECEIVED yang dapat diterima.' : undefined}
        >
          {showForm ? 'Batal' : '+ GR dari PO'}
        </Button>
      )}
      {canCreate && receivablePos.length === 0 && !loading && (
        <HintNote
          text="Tidak ada PO ISSUED/PARTIALLY_RECEIVED yang dapat diterima."
          action={
            <a href="/app/purchase-orders" style={{ color: 'var(--alex-primary)', fontWeight: 600, textDecoration: 'none', whiteSpace: 'nowrap' }}>
              Lihat Purchase Orders →
            </a>
          }
        />
      )}
      {showForm && canCreate && (
        <NewGrForm
          receivablePos={receivablePos}
          warehouses={warehouses}
          onCreated={() => {
            setShowForm(false);
            void load();
          }}
        />
      )}
      {actionError && <ErrorNote text={actionError} />}
      {loading && <LoadingNote />}
      {error && <ErrorNote text={error} />}
      {!loading && !error && (
        <>
          <SummaryStrip
            items={[
              { label: 'Total GR', value: grs.length },
              { label: 'Draft', value: grs.filter((g) => g.status === 'DRAFT').length, tone: 'warning' },
              { label: 'Posted', value: grs.filter((g) => g.status === 'POSTED').length, tone: 'success' },
              { label: 'Cancelled', value: grs.filter((g) => g.status === 'CANCELLED').length, tone: 'neutral' },
            ]}
          />
          <ListToolbar
            search={search}
            onSearchChange={setSearch}
            searchPlaceholder="Cari No. GR..."
            resultCount={total}
            filters={
              <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ width: 150 }}>
                <option value="ALL">Semua status</option>
                <option value="DRAFT">Draft</option>
                <option value="POSTED">Posted</option>
                <option value="CANCELLED">Cancelled</option>
              </Select>
            }
          />
          {grs.length === 0 && <EmptyState text="Belum ada goods receipt." />}
          {grs.length > 0 && total === 0 && <EmptyState text="Tidak ada GR yang cocok dengan pencarian/filter." />}
          {total > 0 && (
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>No. GR</th>
                  <th style={thStyle}>PO</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Diterima</th>
                  <th style={thStyle}></th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((gr) => (
                  <ClickableRow key={gr.gr_id} onClick={() => setSelectedGr(gr)}>
                    <td style={{ ...tdStyle, color: 'var(--alex-primary)', fontWeight: 600 }}>{gr.gr_number}</td>
                    <td style={tdStyle}>{poNumber(gr.po_id)}</td>
                    <td style={tdStyle}>
                      <StatusBadge status={gr.status} />
                    </td>
                    <td style={tdStyle}>{formatDateTime(gr.posted_at)}</td>
                    <td style={tdStyle}>
                      {canPost && gr.status === 'DRAFT' && (
                        <Button
                          variant="primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            void handlePost(gr.gr_id);
                          }}
                          loading={postingId === gr.gr_id}
                        >
                          Post ke Stock Ledger
                        </Button>
                      )}
                    </td>
                  </ClickableRow>
                ))}
              </tbody>
            </table>
          )}
          <Pagination page={page} pageCount={pageCount} setPage={setPage} start={start} pageSize={pageSize} total={total} />
        </>
      )}

      <GrDetailDrawer gr={selectedGr} open={!!selectedGr} onClose={() => setSelectedGr(null)} />
    </div>
  );
}

function NewGrForm({
  receivablePos,
  warehouses,
  onCreated,
}: {
  receivablePos: PurchaseOrder[];
  warehouses: Warehouse[];
  onCreated: () => void;
}) {
  const [poId, setPoId] = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  const [poLines, setPoLines] = useState<PurchaseOrderLine[]>([]);
  const [qtys, setQtys] = useState<Record<string, string>>({});
  const [linesLoading, setLinesLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!poId) {
      setPoLines([]);
      return;
    }
    setLinesLoading(true);
    listPurchaseOrderLines(poId)
      .then((lines) => setPoLines(lines.filter((l) => l.qty_received < l.qty_ordered)))
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLinesLoading(false));
  }, [poId]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await createGoodsReceipt({
        poId,
        warehouseId,
        lines: poLines
          .filter((l) => Number(qtys[l.po_line_id] ?? 0) > 0)
          .map((l) => ({
            po_line_id: l.po_line_id,
            item_id: l.item_id,
            uom_id: l.uom_id,
            qty_received: Number(qtys[l.po_line_id]),
          })),
      });
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="alex-card" style={{ marginBottom: 20, padding: 16 }}>
      <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
        <div style={{ flex: 1 }}>
          <FieldLabel label="Purchase Order">
            <Select value={poId} onChange={(e) => setPoId(e.target.value)} required>
              <option value="" disabled>
                Pilih...
              </option>
              {receivablePos.map((po) => (
                <option key={po.po_id} value={po.po_id}>
                  {po.po_number}
                </option>
              ))}
            </Select>
          </FieldLabel>
        </div>
        <div style={{ flex: 1 }}>
          <FieldLabel label="Warehouse">
            <Select value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)} required>
              <option value="" disabled>
                Pilih...
              </option>
              {warehouses.map((w) => (
                <option key={w.warehouse_id} value={w.warehouse_id}>
                  {w.name}
                </option>
              ))}
            </Select>
          </FieldLabel>
        </div>
      </div>
      {linesLoading && <LoadingNote text="Memuat baris PO..." />}
      {!linesLoading && poLines.length > 0 && (
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Sisa (ordered - received)</th>
              <th style={thStyle}>Qty diterima sekarang</th>
            </tr>
          </thead>
          <tbody>
            {poLines.map((l) => (
              <tr key={l.po_line_id}>
                <td style={tdStyle}>{l.qty_ordered - l.qty_received}</td>
                <td style={tdStyle}>
                  <TextInput
                    type="number"
                    min="0"
                    max={l.qty_ordered - l.qty_received}
                    step="0.01"
                    value={qtys[l.po_line_id] ?? ''}
                    onChange={(e) => setQtys((prev) => ({ ...prev, [l.po_line_id]: e.target.value }))}
                    style={{ width: 140 }}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <Button type="submit" variant="primary" loading={submitting} disabled={poLines.length === 0}>
        Buat GR (DRAFT)
      </Button>
      {error && <ErrorNote text={error} />}
    </form>
  );
}
